import { Component, OnDestroy, OnInit, Input } from '@angular/core';

import moment from 'moment-timezone';
import { Observable, Subscription, combineLatest, filter } from 'rxjs';
import { DndDropEvent } from 'ngx-drag-drop';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { Source } from '../../models/source.model';
import { SourceGroup } from '../../models/source-group.model';
import { SourceService } from '../../services/source.service';
import { SourceGroupService } from '../../services/source-group.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { SourceCreateDialogComponent } from '../SourceCreateDialog/source-create-dialog.component';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { AddSourcesToGroupDialogComponent } from '../AddSourcesToGroupDialog/add-sources-to-group-dialog.component';
import { CreateEditSourceGroupDialogComponent } from '../CreateSourceGroupDialog/create-edit-source-group-dialog.component';
import { EditSourceDialogComponent } from '../EditSourceDialog/edit-source-dialog.component';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { AgentSourceIndexService } from '../../../../AutomationAgents/services/agent-source-index.service';
import { ManageSourceAgentsService } from '../../../../AutomationAgents/services/manage-source-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../../Monitor/Shared/enums/monitoring-series-type.enum';

class SourceCard extends Source {
    created: string;
    createdBy: string;
    modified: string;
    modifiedBy: string;
    selected: boolean = false;
    sourceGroups: SourceGroup[];
    groupsTooltip: string;
}

class SourceGroupCard extends SourceGroup {
    created: string;
    createdBy: string;
    modified: string;
    modifiedBy: string;
    selected: boolean = false;
    sources: SourceCard[];
    /** How many automation agents are live on this group, and the marker's tooltip for it */
    liveAgentCount: number;
    liveAgentTooltip: string;
}

enum SourceSortOptionEnum {
    name,
    createdDate,
    type
}


@Component({
    selector: 'agpr-manage-sources-dialog',
    templateUrl: './manage-sources-dialog.component.html',
    styleUrls: ['./manage-sources-dialog.component.scss'],
    standalone: false
})

export class ManageSourcesDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    public readonly buttonTypes = ButtonTypesEnum;
    public readonly sortOrder = SortOrderEnum;
    public readonly messageSeverityEnum = messageSeverityEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    private subscriptions: Subscription[] = [];

    allSources: SourceCard[] = [];
    filteredSources: SourceCard[] = [];
    sourceSearch: string;
    sourceSortOrder: SortOrderEnum = SortOrderEnum.ascending;
    sourceSortOption: SourceSortOptionEnum = SourceSortOptionEnum.name;
    selectedSources: SourceCard[] = [];
    allSourcesSelected: boolean = false;

    allSourceGroups: SourceGroupCard[] = [];
    filteredSourceGroups: SourceGroupCard[] = [];
    sourceGroupSearch: string;
    sourceGroupSortOrder: SortOrderEnum = SortOrderEnum.ascending;
    sourceGroupSortOption: SourceSortOptionEnum = SourceSortOptionEnum.name;
    selectedSourceGroups: SourceGroupCard[] = [];
    allGroupsSelected: boolean = false;
    activeSourceGroup: SourceGroupCard;
    allSourcesGroup: SourceGroupCard;

    sourceToSourceGroupMap: Map<number, SourceGroup[]>;

    sourceGroupSortMenuOptions: DropdownOptionModel[] = [
        {
            label: 'Source.manageDialog.sortOptions.name',
            value: SourceSortOptionEnum.name
        },
        {
            label: 'Source.manageDialog.sortOptions.createdDate',
            value: SourceSortOptionEnum.createdDate
        }
    ];

    sourceSortMenuOptions: DropdownOptionModel[] = [
        {
            label: 'Source.manageDialog.sortOptions.name',
            value: SourceSortOptionEnum.name
        },
        {
            label: 'Source.manageDialog.sortOptions.createdDate',
            value: SourceSortOptionEnum.createdDate
        },
        {
            label: 'Source.manageDialog.sortOptions.type',
            value: SourceSortOptionEnum.type
        }
    ];
    selectedSourceGroupSortOption: DropdownOptionModel = this.sourceGroupSortMenuOptions[0];
    selectedSourceSortOption: DropdownOptionModel = this.sourceSortMenuOptions[0];

    draggingSelectedSources: boolean = false;
    dateFormat: string;

    /** Whether the account can have automation agents, which the Add Live Agent action is offered for */
    hasAgenticFeature: boolean = false;

    /** Automation agents live on each source group, keyed by AgentSourceIndexService.key */
    private liveAgentCounts: Map<string, number> = new Map<string, number>();

    constructor(
        public coreDialogService: CoreDialogService,
        public sourceService: SourceService,
        public usersService: UsersService,
        public sourceGroupService: SourceGroupService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        private agentSourceIndexService: AgentSourceIndexService,
        private manageSourceAgentsService: ManageSourceAgentsService
    ) {
        super(coreDialogService, 'ManageSources');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([
                this.sourceService.all$,
                this.sourceGroupService.all$
            ]).subscribe(([sources, sourceGroups]) => {
                if (sources) {
                    this.allSources = sources.map(source => {
                        const card = source as SourceCard;
                        return card;
                    });

                    if (!this.allSourcesGroup) {
                        this.allSourcesGroup = new SourceGroupCard();
                        this.allSourcesGroup.name = this.localizationService.transform('Sources.all');
                    }

                    this.allSourcesGroup.sources = this.allSources.slice();

                    if (!this.activeSourceGroup) {
                        this.activeSourceGroup = this.allSourcesGroup;
                    }
                }

                if (sourceGroups) {
                    this.allSourceGroups = sourceGroups.map(group => {
                        const card = group as SourceGroupCard;
                        return card;
                    });

                    if (!!this.activeSourceGroup && this.activeSourceGroup !== this.allSourcesGroup) {
                        this.activeSourceGroup = this.allSourceGroups.find(group => group.id === this.activeSourceGroup.id);
                    }
                }

                this.setData();

                this.filterSources();
                this.sortSources();
                this.filterSourceGroups();
                this.sortSourceGroups();
            }),
            this.sourceGroupService.getSourceToSourceGroupsMap().subscribe(map => {
                this.sourceToSourceGroupMap = map;
                this.setData();
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true);
                this.setData();
            }),
            // Source groups an automation agent is watching, marked on their cards
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => {
                this.liveAgentCounts = counts;
                this.markLiveAgentGroups();
            }),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => this.hasAgenticFeature = enabled)
        );
    }

    setData() {
        if (!this.dateFormat) {
            return;
        }
        const userIds = [];
        this.allSources.forEach(source => {
            userIds.push(source.createdByUserId);
            source.created = moment(source.createdDate).format(this.dateFormat);
            source.selected = this.selectedSources.includes(source);

            if (source.lastModifiedByUserId) {
                userIds.push(source.lastModifiedByUserId);
                if (moment(source.lastModifiedDate).isValid()) {
                    source.modified = moment(source.lastModifiedDate).format(this.dateFormat);
                }
            }

            if (this.sourceToSourceGroupMap) {
                source.sourceGroups = this.sourceToSourceGroupMap.get(source.id);
                if (source.sourceGroups?.length) {
                    const groupNames = source.sourceGroups.map(group => group.name);
                    source.groupsTooltip = groupNames.join(', ');
                }
            }
        });

        this.allSourceGroups.forEach(group => {
            userIds.push(group.createdByUserId);
            group.created = moment(group.createdDate).format(this.dateFormat);
            group.selected = this.selectedSourceGroups.includes(group);
            group.sources = group.sourceIds.map(id => this.allSources.find(source => source.id === id));
            group.sources = group.sources.filter(source => !!source);

            if (group.lastModifiedByUserId) {
                userIds.push(group.lastModifiedByUserId);
                if (moment(group.lastModifiedDate).isValid()) {
                    group.modified = moment(group.lastModifiedDate).format(this.dateFormat);
                }
            }
        });

        this.markLiveAgentGroups();
        this.getUsers(userIds);
    }

    /** Opens the panel that manages the automation agents watching this source group */
    addLiveAgent(group: SourceGroupCard): void {
        this.manageSourceAgentsService.open(MonitoringSeriesTypeEnum.sourceGroup, group?.id);
    }

    /** Stamps the groups an automation agent is live on, so their cards can mark them */
    private markLiveAgentGroups(): void {
        this.allSourceGroups.forEach(group => {
            group.liveAgentCount = this.liveAgentCounts.get(
                this.agentSourceIndexService.key(MonitoringSeriesTypeEnum.sourceGroup, group.id)
            ) ?? 0;
            group.liveAgentTooltip = group.liveAgentCount
                ? this.localizationService.transform('AutomationAgents.liveAgent', { count: `${group.liveAgentCount}` })
                : null;
        });
    }

    getUsers(userIds: number[]) {
        this.usersService.getUsers(userIds).subscribe(users => {
            const userMap = {};
            users.forEach((user) => {
                userMap[user.id] = user.getDisplayName();
            });
            this.allSources.forEach(source => {
                source.createdBy = userMap[source.createdByUserId];
                if (source.lastModifiedByUserId) {
                    source.modifiedBy = userMap[source.lastModifiedByUserId];
                }
            });
            this.allSourceGroups.forEach(group => {
                group.createdBy = userMap[group.createdByUserId];
                if (group.lastModifiedByUserId) {
                    group.modifiedBy = userMap[group.lastModifiedByUserId];
                }
            });
        });
    }

    toggleSourceGroupSortOrder() {
        this.sourceGroupSortOrder = this.sourceGroupSortOrder === SortOrderEnum.ascending ? SortOrderEnum.descending : SortOrderEnum.ascending;
        this.sortSourceGroups();
    }

    sortSourceGroups(option?: DropdownOptionModel) {
        if (option) {
            this.sourceGroupSortOption = option.value;
        }
        this.filteredSourceGroups.sort((a, b) => {
            if (this.sourceGroupSortOption === SourceSortOptionEnum.name) {
                return a.name.trim().localeCompare(b.name.trim());
            } else {
                return a.createdDate.valueOf() - b.createdDate.valueOf();
            }
        });
        if (this.sourceGroupSortOrder !== SortOrderEnum.ascending) {
            this.filteredSourceGroups.reverse();
        }
    }

    toggleSourceSortOrder() {
        this.sourceSortOrder = this.sourceSortOrder === SortOrderEnum.ascending ? SortOrderEnum.descending : SortOrderEnum.ascending;
        this.sortSources();
    }

    sortSources(option?: DropdownOptionModel) {
        if (option) {
            this.sourceSortOption = option.value;
        }
        this.filteredSources.sort((a, b) => {
            if (this.sourceSortOption === SourceSortOptionEnum.name) {
                return (a.name.trim()).localeCompare(b.name.trim());
            } else if (this.sourceSortOption === SourceSortOptionEnum.createdDate) {
                return a.createdDate.valueOf() - b.createdDate.valueOf();
            } else {
                return a.type.localeCompare(b.type);
            }
        });
        if (this.sourceSortOrder !== SortOrderEnum.ascending) {
            this.filteredSources.reverse();
        }
        this.filteredSources = this.filteredSources.slice();
    }

    filterSourceGroups(): void {
        if (!this.allSourceGroups) {
            this.filteredSourceGroups = [];
            return;
        }
        if (!this.sourceGroupSearch) {
            this.filteredSourceGroups = this.allSourceGroups.slice();
            return;
        }

        const queryLower = this.sourceGroupSearch.toLocaleLowerCase();
        this.filteredSourceGroups = this.allSourceGroups.filter(group => {
            return group.name.toLocaleLowerCase().includes(queryLower);
        });

        if (!this.filteredSourceGroups.includes(this.activeSourceGroup)) {
            this.setActiveSourceGroup(this.allSourcesGroup);
        }
    }

    filterSources(): void {
        if (!this.activeSourceGroup?.sources) {
            this.filteredSources = [];
            return;
        }
        if (!this.sourceSearch) {
            this.filteredSources = this.activeSourceGroup.sources.slice();
            return;
        }

        const queryLower = this.sourceSearch.toLocaleLowerCase();
        this.filteredSources = this.activeSourceGroup.sources.filter(source => {
            return source.name.toLocaleLowerCase().includes(queryLower);
        });
    }

    setActiveSourceGroup(group: SourceGroupCard) {
        if (this.activeSourceGroup !== group) {
            this.activeSourceGroup = group;
            this.filteredSources = this.activeSourceGroup.sources.slice();
            this.sourceSearch = '';
            this.sortSources();
            this.selectedSources.forEach(source => {
                source.selected = false;
            });
            this.checkForSelected();
        }
    }

    toggleSelectAllGroups() {
        this.allGroupsSelected = !this.allGroupsSelected;
        this.allSourceGroups.forEach(group => {
            group.selected = this.allGroupsSelected;
        });
        this.checkForSelected();
    }

    toggleSelectAllSources() {
        this.allSourcesSelected = !this.allSourcesSelected;
        this.activeSourceGroup.sources.forEach(source => {
            source.selected = this.allSourcesSelected;
        });
        this.checkForSelected();
    }

    checkForSelected() {
        this.selectedSources = this.filteredSources.filter(source => !!source && source.selected);
        this.allSourcesSelected = this.selectedSources.length === this.filteredSources.length;
        this.selectedSourceGroups = this.filteredSourceGroups.filter(group => !!group && group.selected);
        this.allGroupsSelected = this.selectedSourceGroups.length === this.filteredSourceGroups.length;
    }

    createGroup() {
        this.coreDialogService.open(CreateEditSourceGroupDialogComponent);
    }

    /**
     * Verifies that a new source group name is valid
     */
    getNewSourceGroupNameErrors(name: string): string[] {
        const errors = [];

        if (!name.trim()) {
            errors.push('SourceGroup.error.emptyName');
        }

        if (this.allSourceGroups.find(sourceGroup => sourceGroup.name.toLowerCase() === name.toLowerCase())) {
            errors.push('SourceGroup.error.duplicate');
        }

        return errors;
    }

    editGroup(group: SourceGroupCard) {
        this.coreDialogService.open(CreateEditSourceGroupDialogComponent, {
            inputs: {
                group: group
            }
        });
    }

    /**
     * Deletes source groups
     */
    private deleteSourceGroups(sourceGroups: SourceGroup[]) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'manageSourcesDialog.confirmDialog',
                heading: 'SourceGroups.delete',
                alertMessage: 'SourceGroups.deleteAlert',
                alertType: messageSeverityEnum.error,
                message: 'SourceGroups.confirmMessage',
                list: sourceGroups.map(source => source.name),
                okLabel: 'Core.button.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.sourceGroupService.deleteBulk(sourceGroups).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Deletes all of the selected source group rows
     */
    public deleteSelectedSourceGroups(): void {
        this.deleteSourceGroups(this.selectedSourceGroups);
    }

    /**
     * Deletes an individual source group row
     */
    public deleteSourceGroup(sourceGroup: SourceGroupCard): void {
        this.deleteSourceGroups([sourceGroup]);
    }

    createSource() {
        this.coreDialogService.open(SourceCreateDialogComponent, {
            inputs: {
                currentGroup: this.activeSourceGroup !== this.allSourcesGroup ? this.activeSourceGroup : null
            }
        });
    }

    editSource(source: SourceCard) {
        this.coreDialogService.open(EditSourceDialogComponent, {
            inputs: {
                source: source,
                existingGroups: source.sourceGroups
            }
        });
    }

    removeSource(source: SourceCard) {
        this.activeSourceGroup.sourceIds = this.activeSourceGroup.sourceIds.filter(id => id !== source.id);
        this.sourceGroupService.update(this.activeSourceGroup).subscribe();
    }

    addSourcesToGroup() {
        const io = {
            inputs: {
                sourceList: this.selectedSources
            }
        };
        this.coreDialogService.open(AddSourcesToGroupDialogComponent, io);
    }

    removeSourcesFromGroup() {
        const selectedIds = this.selectedSources.map(source => source.id);
        this.activeSourceGroup.sourceIds = this.activeSourceGroup.sourceIds.filter(id => !selectedIds.includes(id));
        this.sourceGroupService.update(this.activeSourceGroup).subscribe();
    }

    /**
     * Deletes all of the selected rows
     */
    public deleteSelectedSources(): void {
        this.deleteSources(this.selectedSources);
    }

    /**
     * Deletes an individual row
     */
    public deleteSource(source: SourceCard): void {
        this.deleteSources([source]);
    }

    /**
     * Verifies that the user intended to delete a set of tags before sending the delete request
     */
    public deleteSources(sources: Source[]) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'manageSourcesDialog.confirmDialog',
                heading: 'Sources.delete',
                alertMessage: 'Sources.deleteAlert',
                alertType: messageSeverityEnum.error,
                message: 'Sources.confirmMessage',
                list: sources.map(source => source.name),
                okLabel: 'Core.button.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.sourceService.deleteBulk(sources).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    onSourceDragStart(selected: boolean) {
        if (selected) {
            this.draggingSelectedSources = true;
        }
    }

    public cleanupAfterDragCompleted(event: any): void {
        this.draggingSelectedSources = null;
        setTimeout(() => {
            if (event?.target?.style) {
                event.target.style.pointerEvents = 'unset';
            }
        }, 500);
    }

    onSourceDrop(event: DndDropEvent, groupId: number) {
        let groupChanged = false;
        const group = this.allSourceGroups.find(sourceGroup => sourceGroup.id === groupId);
        if (this.draggingSelectedSources) {
            const ids = this.selectedSources.map(source => source.id);
            ids.forEach(id => {
                if (!group.sourceIds.includes(id)) {
                    group.sourceIds.push(id);
                    groupChanged = true;
                }
            });
            this.draggingSelectedSources = false;
        } else if (!group.sourceIds.includes(event.data)) {
            group.sourceIds.push(event.data);
            groupChanged = true;
        }
        if (groupChanged) {
            this.sourceGroupService.update(group).subscribe();
        }
    }

    public cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
